import { InitialFiltersState } from '@state/reducers/initial-filter/InitialFiltersReducer';
import { add, parse } from 'date-fns';
import { BaseTimePeriod, MainFilters, TimePeriod } from '@services/FiltersTypes';
import { Periodicity, PeriodicityKey } from '@components/time-period-selector/PeriodsTypes';
import { CountryFilterPair, FilterKeys, Filters, Option, PageType, SelectedFilters } from '../pageConfig/Types';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import { byCountry } from '@state/reducers/initial-filter/InitialFiltersPredicates';
import { byFilterKey } from '../pageConfig/Predicates/SelectedFiltersPredicates';
import { getLatestPeriodWithMatYtd } from '../FilterUtils';
import { getPeriodsWithoutYtdMat } from '../PeriodUtils';
import { getCountryWithMissingPgs } from '@utils/CountryUtils';

enum MultiCountryClientIds {
  AmazonTCG = 193780,
  AmazonNonTCG = 193790,
  Lidl = 123650,
  Amazon = 3330,
  GfkRetail = 203240,
  AmazonAdvertise = 186740,
}

export const multiCountryDisabledFilters = [
  FilterKeys.CHANNEL,
  FilterKeys.PRICEBAND,
  FilterKeys.REGION,
  FilterKeys.CITY,
];

export const validMultiCountryClientIds: number[] = [
  MultiCountryClientIds.AmazonTCG,
  MultiCountryClientIds.AmazonNonTCG,
  MultiCountryClientIds.Lidl,
  MultiCountryClientIds.Amazon,
  MultiCountryClientIds.GfkRetail,
  MultiCountryClientIds.AmazonAdvertise,
];

/**
 * To Check if client id is entitled to use multi country selection features
 *
 * @return boolean
 *
 * @param clientId - client id
 *
 * **/
export const checkIsMultiCountryClient = (clientId: number): boolean => {
  if (!clientId) {
    return false;
  }
  return validMultiCountryClientIds.includes(+clientId);
};

/**
 * To compare monthly latest period between countries to get the latest month and return the TimePeriod of that country
 *
 * @return TimePeriod
 *
 * @param initialFilterState - Initial Filter State
 * **/
export const getMonthlyLatestPeriodFiltersAmongCountries = (initialFilterState: InitialFiltersState): TimePeriod => {
  const filters = initialFilterState.initialFilters ?? [];
  if (filters.length === 0 || filters[0].country === '' || filters.every((e) => !e.timeFrame?.monthly)) {
    return {
      weekly: [],
      monthly: [],
      rollingQuarter: [],
      calendarQuarter: [],
      customMonths: [],
      customWeeks: [],
    };
  }

  const countriesLatestMonths = initialFilterState.initialFilters.map((e) => {
    if (e.timeFrame.monthly && e.timePeriod.monthly.length > 0) {
      return e.timePeriod.monthly[0].lastUpdatedDate;
    }
  });

  const latestMonth = countriesLatestMonths.filter(Boolean).reduce((a, b) => (a! > b! ? a : b), undefined);

  const latestMonthIndex = countriesLatestMonths.findIndex((e) => e === latestMonth);

  return initialFilterState.initialFilters[latestMonthIndex].timePeriod;
};

/**
 * To compare weekly latest period between countries to get the latest weekly and return the TimePeriod of that country
 *
 * @return TimePeriod
 *
 * @param initialFilterState - Initial Filter State
 * **/
export const getWeeklyLatestPeriodFiltersAmongCountries = (
  initialFilterState: InitialFiltersState,
): BaseTimePeriod[] => {
  if (
    initialFilterState.initialFilters[0]?.country === '' ||
    initialFilterState.initialFilters.every((e) => !e.timeFrame.weekly)
  ) {
    return [];
  }

  const countriesLatestWeeks = initialFilterState.initialFilters.map((e) => {
    if (e.timeFrame.weekly && e.timePeriod.weekly.length > 0) {
      const weeklyPeriods = getPeriodsWithoutYtdMat(e.timePeriod.weekly);
      return convertWeeklyPeriodToDate(weeklyPeriods[weeklyPeriods.length - 1].value);
    }
  });

  const latestWeek = countriesLatestWeeks?.filter(Boolean)?.reduce((a, b) => (a! > b! ? a : b), undefined);

  const latestWeeklyIndex = countriesLatestWeeks.findIndex((e) => e === latestWeek);

  return initialFilterState.initialFilters[latestWeeklyIndex].timePeriod.weekly;
};

/**
 * Convert Weekly Period to Date
 *
 * @return Date - date
 *
 * @param weeklyPeriod - string
 * **/
export const convertWeeklyPeriodToDate = (weeklyPeriod: string): Date => {
  const splitWeek = weeklyPeriod.split(' ');

  return add(parse(splitWeek[2], 'yyyy', new Date()), {
    weeks: +splitWeek[1],
  });
};

/**
 * Convert Monthly Period to Date
 *
 *
 * @return Date - date
 *
 * @param monthlyPeriod - string
 * **/
export const convertMonthlyPeriodToDate = (monthlyPeriod: string): Date => {
  return parse(monthlyPeriod, 'MMM yyyy', new Date());
};

/**
 * To check if selected period from Timeframe picker exist in applied countries based on Periodicity
 *
 * return boolean
 *
 * @param initialFilterState - Initial Filter State
 * @param period - string
 * @param periodicity - Periodicity
 * @param appliedCountries - Applied countries in side filter,
 * eg: [{value: 'DE', label: 'Germany'}, {value: 'AU', label: Australia}]
 * **/
export const isSelectedPeriodExistInAllMultiCountries = (
  initialFilterState: InitialFiltersState,
  period: string,
  periodicity: Periodicity,
  appliedCountries: Option[],
): boolean => {
  const countriesCode = appliedCountries.map((country) => country.value);

  if (
    periodicity === Periodicity.RollingQuarter ||
    periodicity === Periodicity.CalendarQuarter ||
    periodicity === Periodicity.CustomMonths ||
    periodicity === Periodicity.YTD ||
    periodicity === Periodicity.MAT ||
    period.includes(PeriodicityKey.YTD) ||
    period.includes(PeriodicityKey.MAT)
  ) {
    const splitMonth = period.split('-');
    const latestPeriodInQuarter = splitMonth[1].trim();

    if (period.includes('Week')) {
      //Split the period string into 3 components. Eg: ['Week','2','2023']
      const latestWeekPeriodSplit = latestPeriodInQuarter.split(' ');

      //We need to pad the week's number with 0s infront if it is a single digit
      const weekNumber = String(latestWeekPeriodSplit[1]).padStart(2, '0');
      latestWeekPeriodSplit[1] = weekNumber;
      const latestWeekPeriod = latestWeekPeriodSplit.join(' ');

      return checkIsSelectedPeriodExistInAllMultiCountries(
        initialFilterState,
        latestWeekPeriod,
        Periodicity.Weekly,
        countriesCode,
      );
    }

    return checkIsSelectedPeriodExistInAllMultiCountries(
      initialFilterState,
      latestPeriodInQuarter,
      Periodicity.Monthly,
      countriesCode,
    );
  }

  return checkIsSelectedPeriodExistInAllMultiCountries(initialFilterState, period, periodicity, countriesCode);
};

/**
 * To check if selected period from Timeframe picker exist in applied countries, if yes return true, if not return false
 * Only take the latest period from selected period and check across list of time period of different countries to see
 * if all countries has the same time period.
 *
 * @return boolean
 *
 * @param initialFilterState - Initial Filter State
 * @param period - string
 * @param periodicity - Periodicity
 * @param countriesCode - array of countries code, eg: ['DE', 'AU']
 * **/
export const checkIsSelectedPeriodExistInAllMultiCountries = (
  initialFilterState: InitialFiltersState,
  period: string,
  periodicity: Periodicity,
  countriesCode: string[],
): boolean => {
  const filterByCountry = initialFilterState.initialFilters.filter((initialFilter) =>
    countriesCode.includes(initialFilter.country),
  );
  if (periodicity === Periodicity.Weekly) {
    return filterByCountry.some((e) => e.timeFrame.weekly && e.timePeriod.weekly.some((m) => m.value === period));
  }

  return filterByCountry.some((e) => e.timeFrame.monthly && e.timePeriod.monthly.some((m) => m.value === period));
};

/**
 * To get the latest period by periodicity when a single country is left in the filter chips
 *
 * @param countryOptions - current selected countries
 * @param mainFiltersState - Main filter state
 * @param initialFilterState - Initial filter state
 * @return - BaseTimePeriod object for updating the time frame picker
 * **/
export const getLatestPeriodByPeriodicityForSingleCountry = (
  countryOptions: Filters,
  mainFiltersState: MainFiltersState,
  initialFilterState: MainFilters[],
): BaseTimePeriod => {
  const isOneCountryLeft = countryOptions?.options.length === 1;
  let latestTimePeriodOption: BaseTimePeriod = {
    label: '',
    value: '',
    lastUpdatedDate: 0,
  };
  if (isOneCountryLeft) {
    let timePeriodOptions: TimePeriod = initialFilterState.filter(byCountry(countryOptions.options[0].value))[0]
      .timePeriod;

    let periodicity = getPeriodicityForSingleCountry(mainFiltersState, timePeriodOptions);
    const period = mainFiltersState.period;

    const { weekly, monthly } = timePeriodOptions;
    switch (periodicity) {
      case Periodicity.Weekly:
        if (period?.value.includes(PeriodicityKey.MAT)) {
          latestTimePeriodOption = getLatestPeriodWithMatYtd(monthly, weekly, period.label);
        } else if (period?.value.includes(PeriodicityKey.YTD)) {
          latestTimePeriodOption = getLatestPeriodWithMatYtd(monthly, weekly, period.label);
        } else {
          const weeklyPeriods = getPeriodsWithoutYtdMat(weekly);
          latestTimePeriodOption = weeklyPeriods[weeklyPeriods.length - 1];
        }
        break;
      case Periodicity.Monthly:
        // Handling for YTD/MAT will be under Monthly since timePeriodOption stores the YTD and MAT data under monthly.
        // Eg:
        //  timePeriod: {
        //   weekly: [],
        //   monthly: [
        //    { label: 'May 2021', value: 'May 2021', lastUpdatedDate: 1632230784000 },
        //    { label: 'Jun 2021', value: 'Jun 2021', lastUpdatedDate: 1632230784000 },
        //    { label: 'Jul 2021', value: 'Jul 2021', lastUpdatedDate: 1632230784000 },
        //    { label: 'Jan 2021 - Jul 2021', value: 'YTD', lastUpdatedDate: 1632230784000 },
        //    { label: 'Jan 2021 - Jun 2021', value: 'YTD', lastUpdatedDate: 1632230784000 },
        //    { label: 'Aug 2020 - Jul 2021', value: 'MAT', lastUpdatedDate: 1632230784000 },
        //    { label: 'Jul 2020 - Jun 2021', value: 'MAT', lastUpdatedDate: 1632230784000 },
        //  ]}
        if (period?.value.includes(PeriodicityKey.MAT)) {
          latestTimePeriodOption = getLatestPeriodWithMatYtd(monthly, weekly, period.label);
        } else if (period?.value.includes(PeriodicityKey.YTD)) {
          latestTimePeriodOption = getLatestPeriodWithMatYtd(monthly, weekly, period.label);
        } else {
          const monthlyPeriods = getPeriodsWithoutYtdMat(monthly);
          latestTimePeriodOption = monthlyPeriods[monthlyPeriods.length - 1];
        }
        break;
      case Periodicity.RollingQuarter:
        latestTimePeriodOption = timePeriodOptions.rollingQuarter[0];
        break;
      case Periodicity.CalendarQuarter:
        latestTimePeriodOption = timePeriodOptions.calendarQuarter[0];
        break;
      default:
        // Display no options, i.e. unsupported periodicity was supplied
        break;
    }
  }
  return latestTimePeriodOption;
};

/**
 * To check if the selected time frame and period exist in all selected countries
 *
 * @param appliedFilters - current applied Filters
 * @param mainFiltersState - Main filter state
 * @param initialFilterState - Initial filter state
 * @return - Boolean, true if timeframe exist in all selected countries, false otherwise
 * **/
export const checkIsSelectedTimeFrameAndPeriodExistInAllMultiCountries = (
  appliedFilters: SelectedFilters[],
  mainFiltersState: MainFiltersState,
  initialFilterState: InitialFiltersState,
  pageType?: PageType,
): boolean => {
  const pageFilter = appliedFilters.find(
    (selectedFilter: SelectedFilters) => selectedFilter.pageType === (pageType ?? PageType.PERFORMANCE_ANALYSIS),
  );
  const appliedCountries = pageFilter?.selectedFilters.find(byFilterKey(FilterKeys.COUNTRY))?.options;
  const selectedPeriod = mainFiltersState.period;
  const selectedPeriodicity = mainFiltersState.timeFrame;

  if (!selectedPeriod || !selectedPeriodicity || !appliedCountries) {
    return false;
  }

  if (appliedCountries.length > 1) {
    return isSelectedPeriodExistInAllMultiCountries(
      initialFilterState,
      selectedPeriod.label,
      selectedPeriodicity,
      appliedCountries,
    );
  }
  return true;
};

export const getPeriodicityForSingleCountry = (
  mainFiltersState: MainFiltersState,
  timePeriod: TimePeriod,
): Periodicity => {
  let periodicity = mainFiltersState.timeFrame!;

  if (mainFiltersState.timeFrame === Periodicity.Weekly && timePeriod.weekly.length === 0) {
    periodicity = Periodicity.Monthly;
  }

  if (mainFiltersState.timeFrame === Periodicity.Monthly && timePeriod.monthly.length === 0) {
    periodicity = Periodicity.Weekly;
  }

  return periodicity;
};

export const getLatestPeriodAmongAppliedCountries = (
  appliedCountries: Option[],
  initialFilterState: InitialFiltersState,
): TimePeriod => {
  const appliedCountriesTimePeriod = initialFilterState.initialFilters.filter((timePeriod: any) =>
    appliedCountries.some((appliedCountry) => appliedCountry.value === timePeriod.country),
  );
  return {
    weekly: getWeeklyLatestPeriodFiltersAmongCountries({
      initialFilters: appliedCountriesTimePeriod,
    }),
    monthly: getMonthlyLatestPeriodFiltersAmongCountries({
      initialFilters: appliedCountriesTimePeriod,
    }).monthly,
    rollingQuarter: getMonthlyLatestPeriodFiltersAmongCountries({
      initialFilters: appliedCountriesTimePeriod,
    }).rollingQuarter,
    calendarQuarter: getMonthlyLatestPeriodFiltersAmongCountries({
      initialFilters: appliedCountriesTimePeriod,
    }).calendarQuarter,
    customMonths: getMonthlyLatestPeriodFiltersAmongCountries({
      initialFilters: appliedCountriesTimePeriod,
    }).customMonths,
    customWeeks: getMonthlyLatestPeriodFiltersAmongCountries({
      initialFilters: appliedCountriesTimePeriod,
    }).customWeeks,
  };
};

export const getDefaultLatestTimePeriodForMultiCountry = (
  timeFrame: Periodicity,
  appliedProductGroups: Option[],
  appliedCountries: Option[],
  pgListByCountry: CountryFilterPair[],
  initialFilterState: InitialFiltersState,
): TimePeriod => {
  if (
    !timeFrame ||
    pgListByCountry.length === 0 ||
    appliedProductGroups.length === 0 ||
    appliedCountries.length === 1
  ) {
    return initialFilterState.initialFilters.filter(byCountry(appliedCountries[0].value))[0].timePeriod;
  }

  //Here we will filter off the pgs that does not exist in the country's subscription.
  //Then, we take the latest timePeriod within the list of filtered countries
  const countriesWithMissingPgs = getCountryWithMissingPgs(
    timeFrame,
    appliedProductGroups,
    appliedCountries,
    pgListByCountry,
  ).map((country) => country.value);

  const countriesWithAppliedSubscribedPgs = appliedCountries?.filter(
    (country) => !countriesWithMissingPgs.includes(country.value),
  );
  return getLatestPeriodAmongAppliedCountries(countriesWithAppliedSubscribedPgs, initialFilterState);
};
